import * as React from "react";

import { cn } from "@/app/lib/utils";

// forwardRef, unlike the upstream registry copy: shadcn/Origin target React 19,
// where `ref` is an ordinary prop. This app is on React 18, so without it the ref
// never attaches and react-hook-form's register() reads every value as undefined.
// Re-apply this after any `shadcn add input`.
const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
	({ className, type, ...props }, ref) => {
		return (
			<input
				ref={ref}
				className={cn(
					"flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none transition-[color,box-shadow] file:inline-flex file:h-7 file:border-0 file:bg-transparent file:font-medium file:text-foreground file:text-sm placeholder:text-muted-foreground/70 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
					"focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
					"aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
					type === "search" &&
						"[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none",
					type === "file" &&
						"p-0 pr-3 text-muted-foreground/70 italic file:me-3 file:h-full file:border-0 file:border-input file:border-r file:border-solid file:bg-transparent file:px-3 file:font-medium file:text-foreground file:text-sm file:not-italic",
					// Chromium lays the calendar/clock button out inline, right after the
					// value, so on a full-width field it floats in the middle instead of at
					// the edge. Taking it out of flow pins it to the same 12px inset as the
					// text; `pr-9` keeps the value from running underneath it.
					(type === "date" || type === "datetime-local" || type === "month" || type === "time" || type === "week") &&
						"relative pr-9 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-y-0 [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:my-auto [&::-webkit-calendar-picker-indicator]:size-4 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100",
					className,
				)}
				data-slot="input"
				type={type}
				{...props}
			/>
		);
	},
);

Input.displayName = "Input";

export { Input };
